// The Strategy tab's data, the same for the API (functions/src/strategy/) and the web app (web/src/strategy/).
//   content   the strategy graph a Strategy Integrator run writes (graph.json): institutions' directions, goals
//             and projects, the links between them, overlap clusters, gaps. Stored whole, one version per
//             drop; the newest is shown.
//   changes   what people decide in the views: link decisions, element edits, new institutions and links,
//             priority orders, RACI, weights. Each under its own key, so two people editing different
//             things never overwrite each other. The pilot page kept them in one browser (pilot-decisions.json).
//   library   the library's labels, web links and removed sources (domain/library.ts), kept by the page too.
//   inbox     people's decisions on what Claude proposes from a document (domain/reading.ts), kept by the page.
//   plan      the planning workbook's KPIs, deadlines and texts beside the consolidation (domain/plan.ts), kept by the page.
import { libraryEntryProblem } from './library.ts';
import { inboxEntryProblem } from './reading.ts';
import { planEntryProblem } from './plan.ts';
import { MAX_TEXT } from './vocab.ts';

/** The library's folder: documents the strategy is built from. A folder of no task (functions/src/folders/rules.ts). */
export const STRATEGY_LIBRARY = 'STRATEGY';

/** The sections the views keep (the pilot's state). */
export const PILOT_SECTIONS = ['decisions', 'edits', 'institutions', 'links', 'orders', 'raci', 'weights'] as const;

/**
 * The sections of the changes, one Table partition each: the views' own, and four the page keeps: the
 * consolidation (domain/consolidate.ts), the library (domain/library.ts), the inbox (domain/reading.ts) and the
 * plan (domain/plan.ts).
 */
export const STRATEGY_SECTIONS = [...PILOT_SECTIONS, 'consolidation', 'library', 'inbox', 'plan'] as const;
export type StrategySection = (typeof STRATEGY_SECTIONS)[number];

/** Every change, by section and key. */
export type StrategyState = Record<StrategySection, Record<string, unknown>>;

/** One key set (`value`) or removed (null). */
export interface StrategyChange {
  section: StrategySection;
  key: string;
  value: unknown;
  /**
   * The value's fields set on the key's saved value; a field null removed. Fields it does not name stay (mergedValue).
   * A link's decision only (MERGE_SECTIONS), and its key once in a send (strategyChangesProblem).
   */
  merge?: true;
}

export const emptyStrategyState = (): StrategyState =>
  Object.fromEntries(STRATEGY_SECTIONS.map((s) => [s, {}])) as unknown as StrategyState;

/** The key `weights` holds: one set for everyone, as the pilot had. */
export const WEIGHTS_KEY = 'all';

/** The longest key (a link or element id, an institution); a Table row key allows 1 KB. */
export const STRATEGY_KEY_MAX = 200;

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Why a change cannot be stored, or ''. The value is kept as JSON text: at most MAX_TEXT characters. */
export function strategyChangeProblem(c: unknown): string {
  if (!isObject(c)) return 'A change is an object.';
  if (!(STRATEGY_SECTIONS as readonly string[]).includes(String(c.section))) return `Unknown section: ${String(c.section)}`;
  const key = c.key;
  if (typeof key !== 'string' || !key.trim()) return 'A change needs a key.';
  if (key.length > STRATEGY_KEY_MAX) return `A key is at most ${STRATEGY_KEY_MAX} characters.`;
  if (/[\u0000-\u001f\u007f-\u009f]/.test(key)) return 'A key holds no control characters.';
  if (c.value === undefined) return 'A change needs a value (null removes the key).';
  if (c.value !== null && JSON.stringify(c.value).length > MAX_TEXT) return tooLong(key);
  if (c.merge !== undefined) {
    if (c.merge !== true) return 'merge is true or left out.';
    if (!(MERGE_SECTIONS as readonly string[]).includes(c.section as string)) return `A merge is for ${MERGE_SECTIONS.join(', ')}, not ${String(c.section)}.`;
    if (!isObject(c.value)) return 'A merge sets fields: its value is an object.';
    if (!Object.keys(c.value).length) return 'A merge sets at least one field.';
  }
  if (c.section === 'library') return libraryEntryProblem(key, c.value);
  if (c.section === 'inbox') return inboxEntryProblem(key, c.value);
  if (c.section === 'plan') return planEntryProblem(key, c.value);
  return '';
}

/** The sections whose values a merge may change field by field: objects of fields (a link's decision and note). */
export const MERGE_SECTIONS = ['decisions'] as const satisfies readonly StrategySection[];

/**
 * Why a send of changes cannot be stored, or '': a change's problem (strategyChangeProblem), or a merged key that is
 * in it twice. The server merges each from the key's row as it was before the send, so the second would undo the first.
 */
export function strategyChangesProblem(list: readonly unknown[]): string {
  const merged = new Map<string, boolean>();
  for (const c of list) {
    const problem = strategyChangeProblem(c);
    if (problem) return problem;
    const { section, key, merge } = c as StrategyChange;
    const id = `${section} ${key}`;
    if (merged.has(id) && (merge || merged.get(id))) return `“${key}” is merged: it is in a send once.`;
    merged.set(id, !!merge || !!merged.get(id));
  }
  return '';
}

/** Why a key's value cannot be stored: too long. */
export const tooLong = (key: string) => `“${key}” is too long to store (over ${MAX_TEXT.toLocaleString('en-US')} characters).`;

/** A saved value with a merge change's fields set on it; a field null removed. No field left: null, the key goes. */
export function mergedValue(saved: unknown, fields: Record<string, unknown>): Record<string, unknown> | null {
  const out: Record<string, unknown> = isObject(saved) ? { ...saved } : {};
  for (const [k, v] of Object.entries(fields)) {
    if (v === null) delete out[k];
    else out[k] = v;
  }
  return Object.keys(out).length ? out : null;
}

/** The changes from `before` to `after` in `sections`: one per key whose value differs; null for a key that went. */
export function strategyChanges(before: StrategyState, after: StrategyState, sections: readonly StrategySection[] = STRATEGY_SECTIONS): StrategyChange[] {
  const out: StrategyChange[] = [];
  for (const section of sections) {
    const a = before[section] ?? {};
    const b = after[section] ?? {};
    for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
      const was = key in a ? JSON.stringify(a[key]) : undefined;
      const now = key in b && b[key] !== undefined ? JSON.stringify(b[key]) : undefined;
      if (was !== now) out.push({ section, key, value: now === undefined ? null : b[key] });
    }
  }
  return out;
}

/** `state` with `changes` applied (a new object; `state` is left as it is). */
export function applyStrategyChanges(state: StrategyState, changes: readonly StrategyChange[]): StrategyState {
  const out = Object.fromEntries(STRATEGY_SECTIONS.map((s) => [s, { ...state[s] }])) as unknown as StrategyState;
  for (const c of changes) {
    const value = c.merge ? mergedValue(out[c.section][c.key], c.value as Record<string, unknown>) : c.value;
    if (value === null) delete out[c.section][c.key];
    else out[c.section][c.key] = value;
  }
  return out;
}

/**
 * The pilot page's own state: its DEC, ED, CUST, ORDS, RACI and WT, as pilot-decisions.json holds them
 * (an older file has `order`, VPC's priority order alone).
 */
export interface PilotState {
  decisions?: unknown;
  edits?: unknown;
  custom?: { inst?: unknown; links?: unknown } | null;
  orders?: unknown;
  order?: unknown;
  raci?: unknown;
  weights?: unknown;
}

/** Items with an id, by id; the rest are dropped (the pilot gives every custom institution and link one). */
function byId(list: unknown): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (Array.isArray(list)) for (const x of list) if (isObject(x) && typeof x.id === 'string' && x.id) out[x.id] = x;
  return out;
}

const plain = (v: unknown): Record<string, unknown> => (isObject(v) ? { ...v } : {});

/** The pilot's state as the shared changes. */
export function fromPilot(p: PilotState): StrategyState {
  const orders = isObject(p.orders) ? { ...p.orders } : Array.isArray(p.order) ? { VPC: p.order } : {};
  return {
    decisions: plain(p.decisions),
    edits: plain(p.edits),
    institutions: byId(p.custom?.inst),
    links: byId(p.custom?.links),
    orders,
    raci: plain(p.raci),
    weights: isObject(p.weights) ? { [WEIGHTS_KEY]: p.weights } : {},
    consolidation: {},
    library: {},
    inbox: {},
    plan: {},
  };
}

/** The shared changes as the pilot page holds them; weights null: the content's own. Lists in key order. */
export function toPilot(s: StrategyState) {
  const list = (o: Record<string, unknown>) => Object.keys(o).sort().map((k) => o[k]);
  return {
    decisions: { ...s.decisions },
    edits: { ...s.edits },
    custom: { inst: list(s.institutions), links: list(s.links) },
    orders: { ...s.orders },
    raci: { ...s.raci },
    weights: isObject(s.weights[WEIGHTS_KEY]) ? (s.weights[WEIGHTS_KEY] as Record<string, unknown>) : null,
  };
}

/** The lists strategy content holds; each must be one when present. */
const CONTENT_LISTS = ['sources', 'external', 'directions', 'projects', 'relations', 'overlaps', 'gaps'] as const;

/**
 * Why `v` is not strategy content (graph.json), or ''. It must be an object with its elements
 * (`external` and/or `projects`) and its links (`relations`) as lists.
 */
export function strategyContentProblem(v: unknown): string {
  if (!isObject(v)) return 'This is not strategy content: a graph.json is one object.';
  for (const k of CONTENT_LISTS) if (k in v && !Array.isArray(v[k])) return `This is not strategy content: “${k}” is not a list.`;
  if (!Array.isArray(v.relations) || (!Array.isArray(v.external) && !Array.isArray(v.projects))) {
    return 'This is not strategy content: a graph.json has elements (“external”, “projects”) and links (“relations”).';
  }
  return '';
}

/** The plan's default priority weights (strategy-integrator-plan, section 4), for content without its own. */
export const DEFAULT_STRATEGY_WEIGHTS = { mandate: 0.25, align: 0.2, enabling: 0.15, impact: 0.15, readiness: 0.1, funding: 0.1, risk: 0.05 };

/** Content with every list and object the views read: a missing one is empty. */
export function normalizeContent(v: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...v };
  for (const k of CONTENT_LISTS) if (!Array.isArray(out[k])) out[k] = [];
  if (!isObject(out.gap_notes)) out.gap_notes = {};
  if (!isObject(out.meta)) out.meta = {};
  if (!isObject(out.weights)) out.weights = { ...DEFAULT_STRATEGY_WEIGHTS };
  return out;
}

/** What a content version holds, for the line under the map and the change log. */
export function contentCounts(v: Record<string, unknown>): { elements: number; links: number; overlaps: number } {
  const n = (k: string) => (Array.isArray(v[k]) ? (v[k] as unknown[]).length : 0);
  return { elements: n('external') + n('projects') + n('directions'), links: n('relations'), overlaps: n('overlaps') };
}

/** A pilot-decisions.json (the pilot's "Save changes"): its state, not content. */
export const isPilotDecisions = (v: unknown): v is PilotState =>
  isObject(v) && !('relations' in v) && ['decisions', 'edits', 'custom', 'orders', 'raci', 'weights'].some((k) => k in v);

/** The most changes one save carries. */
export const STRATEGY_CHANGES_MAX = 500;

/** The largest content file accepted, in bytes of JSON. */
export const STRATEGY_CONTENT_MAX = 5 * 1024 * 1024;
